/**
 * React Hooks for OIDC Authentication
 *
 * Provides React hooks for managing authentication state and performing
 * OIDC operations in React components.
 *
 * @module @project-x/auth/oidc/hooks
 */

"use client";

import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useState,
  type ReactNode,
  type PropsWithChildren,
} from "react";
import type { OIDCConfig, AuthSession, OIDCUser, TokenResponse, OIDCError, LoginOptions } from "./types";
import {
  initiateLogin,
  initiateLogout,
  forceReauth,
  handleCallback,
  decodeToken,
  isAuthFresh,
  roleHelpers,
  OIDCClientError,
  cleanupStaleAuthState,
} from "./client";
import { createOIDCConfig } from "./config";

/**
 * Context value provided by AuthProvider.
 */
export interface AuthContextValue extends AuthSession {
  /** Initiates login flow by redirecting to Keycloak */
  login: (returnTo?: string | LoginOptions) => Promise<void>;
  /** Logs out the user and redirects to Keycloak logout */
  logout: () => void;
  /** Forces re-authentication for sensitive operations */
  reauthenticate: (returnTo?: string) => Promise<void>;
  /** Refreshes the access token using the refresh token */
  refreshSession: () => Promise<void>;
  /** Gets a valid access token (refreshing if needed) */
  getAccessToken: () => Promise<string | null>;
  /** Checks if the user has a specific role */
  hasRole: (role: string) => boolean;
  /** Checks if the user has any of the specified roles */
  hasAnyRole: (roles: readonly string[]) => boolean;
  /** Checks if the user has all of the specified roles */
  hasAllRoles: (roles: readonly string[]) => boolean;
  /** Checks if user is an admin */
  isAdmin: boolean;
  /** Checks if user is a manager */
  isManager: boolean;
  /** Checks if user's auth is fresh enough for sensitive operations */
  isAuthFresh: (maxAgeSeconds?: number) => boolean;
}

/**
 * Default context value (used when no provider is present).
 */
const defaultContextValue: AuthContextValue = {
  user: null,
  accessToken: null,
  expiresAt: null,
  isLoading: true,
  isAuthenticated: false,
  error: null,
  login: async () => {
    if (shouldLogAuthDebug) {
      console.warn("[Auth] No AuthProvider found");
    }
  },
  logout: () => {
    if (shouldLogAuthDebug) {
      console.warn("[Auth] No AuthProvider found");
    }
  },
  reauthenticate: async () => {
    if (shouldLogAuthDebug) {
      console.warn("[Auth] No AuthProvider found");
    }
  },
  refreshSession: async () => {
    if (shouldLogAuthDebug) {
      console.warn("[Auth] No AuthProvider found");
    }
  },
  getAccessToken: async () => null,
  hasRole: () => false,
  hasAnyRole: () => false,
  hasAllRoles: () => false,
  isAdmin: false,
  isManager: false,
  isAuthFresh: () => false,
};

/**
 * Authentication context.
 */
const AuthContext = createContext<AuthContextValue>(defaultContextValue);

const SESSION_ENDPOINT = "/api/auth/session";
const REFRESH_ENDPOINT = "/api/auth/refresh";
const shouldLogAuthDebug = typeof process !== "undefined" && process.env?.NODE_ENV !== "production";

async function persistSessionCookie(
  tokens: TokenResponse,
  user: OIDCUser,
  endpoint: string = SESSION_ENDPOINT,
): Promise<void> {
  try {
    if (!tokens.refresh_token) {
      if (shouldLogAuthDebug) {
        console.warn("[Auth] No refresh token provided, skipping cookie persistence");
      }
      return;
    }
    await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tokens: {
          refresh_token: tokens.refresh_token,
          refresh_expires_in: tokens.refresh_expires_in ?? tokens.expires_in,
        },
        user: {
          id: user.id,
          encodedId: user.encodedId,
          email: user.email,
          displayName: user.displayName,
          roles: user.roles,
          profileComplete: user.profileComplete,
          emailVerified: user.emailVerified,
        },
      }),
      cache: "no-store",
    });
  } catch (error) {
    if (shouldLogAuthDebug) {
      console.error("[Auth] Failed to persist session cookie", error);
    }
  }
}

async function clearSessionCookie(endpoint: string = SESSION_ENDPOINT): Promise<void> {
  try {
    await fetch(endpoint, { method: "DELETE", cache: "no-store" });
  } catch (error) {
    if (shouldLogAuthDebug) {
      console.error("[Auth] Failed to clear session cookie", error);
    }
  }
}

/**
 * Props for AuthProvider component.
 */
export interface AuthProviderProps extends PropsWithChildren {
  /** OIDC configuration (optional, defaults to env-based config) */
  config?: OIDCConfig;
  /** Endpoint used to persist/clear httpOnly refresh token cookies */
  sessionEndpoint?: string;
  /** Endpoint used to refresh access tokens from the httpOnly cookie */
  refreshEndpoint?: string;
  /** Called when login completes (optional) */
  onLoginComplete?: (user: OIDCUser) => void;
  /** Called when logout completes (optional) */
  onLogoutComplete?: () => void;
  /** Called when an error occurs (optional) */
  onError?: (error: OIDCClientError) => void;
  /** Auto-refresh tokens before expiry (default: true) */
  autoRefresh?: boolean;
  /** Seconds before expiry to trigger refresh (default: 60) */
  refreshBuffer?: number;
}

/**
 * Authentication provider component.
 * Manages authentication state and provides auth methods to children.
 */
export function AuthProvider({
  children,
  config,
  sessionEndpoint = SESSION_ENDPOINT,
  refreshEndpoint = REFRESH_ENDPOINT,
  onLoginComplete,
  onLogoutComplete,
  onError,
  autoRefresh = true,
  refreshBuffer = 60,
}: AuthProviderProps): ReactNode {
  const oidcConfig = useMemo(() => config ?? createOIDCConfig(), [config]);

  const [user, setUser] = useState<OIDCUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<OIDCError | null>(null);

  const isAuthenticated = useMemo(() => !!user && !!accessToken, [user, accessToken]);

  const refreshFromServer = useCallback(async (): Promise<boolean> => {
    try {
      // First check if session cookie exists before attempting refresh
      const sessionCheckResponse = await fetch("/api/auth/session", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
      });
      if (!sessionCheckResponse.ok) {
        return false;
      }
      const sessionCheck = (await sessionCheckResponse.json()) as { hasSession: boolean };
      if (!sessionCheck.hasSession) {
        // No session exists, skip the refresh call
        return false;
      }

      const response = await fetch(refreshEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
      });
      if (!response.ok) {
        return false;
      }
      const payload = (await response.json()) as {
        tokens: TokenResponse;
        user: OIDCUser;
        expiresAt?: number;
      };

      const nextExpiresAt = payload.expiresAt ?? Date.now() + payload.tokens.expires_in * 1000;

      setAccessToken(payload.tokens.access_token);
      setIdToken(payload.tokens.id_token ?? null);
      setUser(payload.user);
      setExpiresAt(nextExpiresAt);
      setError(null);
      return true;
    } catch (err) {
      if (shouldLogAuthDebug) {
        console.error("[Auth] Failed to bootstrap session from server", err);
      }
      return false;
    }
  }, [refreshEndpoint]);

  const refreshSession = useCallback(async () => {
    // Silent refresh - do not set loading state to avoid UI flicker
    const refreshed = await refreshFromServer();
    if (!refreshed) {
      await clearSessionCookie(sessionEndpoint);
      setUser(null);
      setAccessToken(null);
      setIdToken(null);
      setExpiresAt(null);
    }
  }, [refreshFromServer, sessionEndpoint]);

  // Initialize session from httpOnly cookie on mount
  useEffect(() => {
    // Clean up any stale auth state from localStorage (from abandoned auth flows)
    cleanupStaleAuthState();

    let cancelled = false;
    const bootstrap = async () => {
      const refreshed = await refreshFromServer();
      if (!cancelled) {
        setIsLoading(false);
        if (!refreshed) {
          setUser(null);
          setAccessToken(null);
          setIdToken(null);
          setExpiresAt(null);
        }
      }
    };
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [refreshFromServer]);

  // Auto-refresh tokens before expiry
  useEffect(() => {
    if (!autoRefresh || !expiresAt) return;

    const refreshAt = expiresAt - refreshBuffer * 1000;
    const now = Date.now();

    if (refreshAt <= now) {
      // Token already needs refresh
      if (shouldLogAuthDebug) {
        console.log("[Auth] Token needs immediate refresh");
      }
      refreshSession();
      return;
    }

    const timeoutMs = refreshAt - now;
    if (shouldLogAuthDebug) {
      console.log(`[Auth] Scheduling token refresh in ${Math.round(timeoutMs / 1000)}s`);
    }

    const timeout = setTimeout(() => {
      if (shouldLogAuthDebug) {
        console.log("[Auth] Token refresh triggered");
      }
      refreshSession();
    }, timeoutMs);

    return () => clearTimeout(timeout);
  }, [autoRefresh, expiresAt, refreshBuffer, refreshSession]);

  // Session methods
  const login = useCallback(
    async (returnTo?: string | LoginOptions) => {
      setIsLoading(true);
      setError(null);
      try {
        await initiateLogin(oidcConfig, returnTo);
      } catch (err) {
        const error = err instanceof OIDCClientError ? err : new OIDCClientError(String(err), "login_error");
        setError(error.details ?? { error: error.code });
        onError?.(error);
        setIsLoading(false);
      }
    },
    [oidcConfig, onError],
  );

  const logout = useCallback(() => {
    if (shouldLogAuthDebug) {
      console.log("[Auth] 🚪 Logging out user", {
        timestamp: new Date().toISOString(),
        userId: user?.id,
        email: user?.email,
      });
    }
    void clearSessionCookie(sessionEndpoint);
    setUser(null);
    setAccessToken(null);
    setIdToken(null);
    setExpiresAt(null);
    setError(null);
    onLogoutComplete?.();
    initiateLogout(oidcConfig, idToken ?? undefined);
  }, [oidcConfig, idToken, onLogoutComplete, sessionEndpoint, user]);

  const reauthenticate = useCallback(
    async (returnTo?: string) => {
      setIsLoading(true);
      setError(null);
      try {
        await forceReauth(oidcConfig, returnTo);
      } catch (err) {
        const error = err instanceof OIDCClientError ? err : new OIDCClientError(String(err), "reauth_error");
        setError(error.details ?? { error: error.code });
        onError?.(error);
        setIsLoading(false);
      }
    },
    [oidcConfig, onError],
  );

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!accessToken) {
      await refreshSession();
      return accessToken;
    }

    // Check if token needs refresh
    if (expiresAt && Date.now() > expiresAt - 30 * 1000) {
      await refreshSession();
    }

    return accessToken;
  }, [accessToken, expiresAt, refreshSession]);

  // Role helpers
  const hasRole = useCallback((role: string) => roleHelpers.hasRole(user, role), [user]);
  const hasAnyRole = useCallback((roles: readonly string[]) => roleHelpers.hasAnyRole(user, roles), [user]);
  const hasAllRoles = useCallback((roles: readonly string[]) => roleHelpers.hasAllRoles(user, roles), [user]);
  const isAdmin = useMemo(() => roleHelpers.isAdmin(user), [user]);
  const isManager = useMemo(() => roleHelpers.isManager(user), [user]);

  const checkAuthFresh = useCallback(
    (maxAgeSeconds: number = 300): boolean => {
      if (!accessToken) return false;
      try {
        const claims = decodeToken(accessToken);
        return isAuthFresh(claims, maxAgeSeconds);
      } catch {
        return false;
      }
    },
    [accessToken],
  );

  // Handle OIDC callback completion. Returns a promise so the callback page can await
  // session cookie persistence before redirecting (avoids redirect loop when middleware
  // runs before the cookie is set).
  const handleCallbackComplete = useCallback(
    (tokens: TokenResponse, newUser: OIDCUser): Promise<void> => {
      if (shouldLogAuthDebug) {
        console.log("[Auth] ✅ Login callback completed", {
          timestamp: new Date().toISOString(),
          userId: newUser.id,
          email: newUser.email,
          roles: newUser.roles,
          expiresIn: tokens.expires_in,
        });
      }
      setAccessToken(tokens.access_token);
      setIdToken(tokens.id_token ?? null);
      setUser(newUser);
      setExpiresAt(Date.now() + tokens.expires_in * 1000);
      setIsLoading(false);
      setError(null);

      onLoginComplete?.(newUser);
      return persistSessionCookie(tokens, newUser, sessionEndpoint);
    },
    [onLoginComplete, sessionEndpoint],
  );

  // Handle callback errors
  const handleCallbackError = useCallback(
    (err: unknown) => {
      const error = err instanceof OIDCClientError ? err : new OIDCClientError(String(err), "callback_error");
      setError(error.details ?? { error: error.code });
      setIsLoading(false);
      onError?.(error);
    },
    [onError],
  );

  const contextValue = useMemo<AuthContextValue>(
    () => ({
      user,
      accessToken,
      expiresAt,
      isLoading,
      isAuthenticated,
      error,
      login,
      logout,
      reauthenticate,
      refreshSession,
      getAccessToken,
      hasRole,
      hasAnyRole,
      hasAllRoles,
      isAdmin,
      isManager,
      isAuthFresh: checkAuthFresh,
    }),
    [
      user,
      accessToken,
      expiresAt,
      isLoading,
      isAuthenticated,
      error,
      login,
      logout,
      reauthenticate,
      refreshSession,
      getAccessToken,
      hasRole,
      hasAnyRole,
      hasAllRoles,
      isAdmin,
      isManager,
      checkAuthFresh,
    ],
  );

  // Export callback handlers for use in callback page
  const providerValue = useMemo(
    () => ({
      ...contextValue,
      _handleCallbackComplete: handleCallbackComplete,
      _handleCallbackError: handleCallbackError,
    }),
    [contextValue, handleCallbackComplete, handleCallbackError],
  );

  return <AuthContext.Provider value={providerValue as AuthContextValue}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access authentication context.
 * Must be used within an AuthProvider.
 *
 * @returns Authentication context value
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/**
 * Hook to get the current user.
 * Returns null if not authenticated.
 *
 * @returns Current user or null
 */
export function useUser(): OIDCUser | null {
  const { user } = useAuth();
  return user;
}

/**
 * Hook to check if user is authenticated.
 *
 * @returns True if user is authenticated
 */
export function useIsAuthenticated(): boolean {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
}

/**
 * Hook to get a valid access token.
 * Automatically refreshes if needed.
 *
 * @returns Promise resolving to access token or null
 */
export function useAccessToken(): () => Promise<string | null> {
  const { getAccessToken } = useAuth();
  return getAccessToken;
}

/**
 * Hook to check user roles.
 *
 * @returns Object with role checking methods
 */
export function useRoles(): {
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: readonly string[]) => boolean;
  hasAllRoles: (roles: readonly string[]) => boolean;
  isAdmin: boolean;
  isManager: boolean;
} {
  const { hasRole, hasAnyRole, hasAllRoles, isAdmin, isManager } = useAuth();
  return { hasRole, hasAnyRole, hasAllRoles, isAdmin, isManager };
}

/**
 * Props for AuthGate component.
 */
export interface AuthGateProps extends PropsWithChildren {
  /** Content to show while loading */
  fallback?: ReactNode;
  /** Content to show when not authenticated (default: redirects to login) */
  unauthorized?: ReactNode;
  /** Required roles (user must have at least one) */
  roles?: readonly string[];
  /** Require all roles instead of any */
  requireAllRoles?: boolean;
  /** Redirect path when not authenticated (default: login) */
  redirectTo?: string;
  /** Called when access is denied */
  onUnauthorized?: () => void;
}

/**
 * Component that gates content behind authentication.
 * Optionally requires specific roles.
 */
export function AuthGate({
  children,
  fallback,
  unauthorized,
  roles,
  requireAllRoles = false,
  redirectTo,
  onUnauthorized,
}: AuthGateProps): ReactNode {
  const { isLoading, isAuthenticated, hasAnyRole, hasAllRoles, login } = useAuth();

  // Handle redirect for unauthenticated users
  useEffect(() => {
    if (!isLoading && !isAuthenticated && redirectTo !== undefined) {
      login(typeof window !== "undefined" ? window.location.pathname : undefined);
    }
  }, [isLoading, isAuthenticated, redirectTo, login]);

  // Show loading state
  if (isLoading) {
    return fallback ?? null;
  }

  // Not authenticated
  if (!isAuthenticated) {
    if (unauthorized) {
      return unauthorized;
    }
    // Default: redirect to login (handled by effect above)
    return fallback ?? null;
  }

  // Check roles if required
  if (roles && roles.length > 0) {
    const hasRequiredRoles = requireAllRoles ? hasAllRoles(roles) : hasAnyRole(roles);

    if (!hasRequiredRoles) {
      onUnauthorized?.();
      if (unauthorized) {
        return unauthorized;
      }
      return (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <h2>Access Denied</h2>
          <p>You don&apos;t have permission to access this page.</p>
        </div>
      );
    }
  }

  return children;
}

/**
 * Hook for handling OIDC callback page.
 * Should be used in the /auth/callback route.
 *
 * @param config - OIDC configuration (optional)
 * @returns Object with callback state and handlers
 */
export function useOIDCCallback(config?: OIDCConfig): {
  isProcessing: boolean;
  error: OIDCError | null;
  returnTo: string | null;
  processCallback: () => Promise<void>;
} {
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<OIDCError | null>(null);
  const [returnTo, setReturnTo] = useState<string | null>(null);

  // Access internal handlers from context (type assertion needed)
  const auth = useAuth() as AuthContextValue & {
    _handleCallbackComplete?: (tokens: TokenResponse, user: OIDCUser) => void | Promise<void>;
    _handleCallbackError?: (error: unknown) => void;
  };

  const processCallback = useCallback(async () => {
    setIsProcessing(true);
    setError(null);

    try {
      const result = await handleCallback(config);
      setReturnTo(result.returnTo ?? null);
      await auth._handleCallbackComplete?.(result.tokens, result.user);
    } catch (err) {
      const oidcError = err instanceof OIDCClientError ? err.details : { error: "callback_error" };
      setError(oidcError ?? { error: "unknown_error" });
      auth._handleCallbackError?.(err);
    } finally {
      setIsProcessing(false);
    }
  }, [config, auth]);

  // Process callback on mount
  useEffect(() => {
    processCallback();
  }, []);

  return { isProcessing, error, returnTo, processCallback };
}

